import { ReactNode } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall, BodySmallBold } from 'styles/typography';

// Pieces both benefits views share: the benefits tables, the per-service-type table under "Show all
// benefits", and the blue text links in the slideout (Edit, Download PDF, Show all benefits).

export const BENEFIT_COLUMNS = ['Type', 'Coverage Level', 'Network', 'Coverage', 'Benefit'];
// Fixed shares (percent) so the columns line up from one service type's table to the next.
export const BENEFIT_WIDTHS = [23, 14, 14, 20, 29];

export function StcTable({ title, rows, widths }: { title: string; rows: ReactNode[][]; widths?: number[] }) {
  return (
    <Group>
      <GroupTitle>{title}</GroupTitle>
      <TableScroll>
        <Table>
          {widths && (
            <colgroup>
              {widths.map((width, i) => (
                <col key={i} style={{ width: `${width}%` }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              {BENEFIT_COLUMNS.map((col) => (
                <Th key={col}>{col}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <Td key={j}>{cell}</Td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </Group>
  );
}

export const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  flex-shrink: 0;
`;

const Group = styled.div`
  padding: 20px 24px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const GroupTitle = styled(BodySmallBold)`
  display: block;
  margin-bottom: 12px;
  color: ${colors.black100};
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.black50};
  border-bottom: 1px solid ${colors.black10};
  white-space: nowrap;
`;

export const Td = styled(BodySmall).attrs({ as: 'td' })`
  padding: 12px;
  vertical-align: top;
  color: ${colors.black100};
  border-bottom: 1px solid ${colors.black5};
`;

// The benefits views' section card: a light blue header naming the section, over a bordered body.
export const SectionCard = styled.section`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  overflow: hidden;
`;

export const SectionCardHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 24px;
  background: ${colors.blue5};
  border-bottom: 1px solid ${colors.black10};
`;

export const SectionCardTitle = styled.span`
  font-size: 15px;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  color: ${colors.black100};
`;

export const SectionCardHint = styled.span`
  font-size: 13px;
  color: ${colors.black50};
`;
